import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo } from 'react';
import { useCallbackRef } from '@/js/libs/hooks/useCallbackRef';
import { memoGeneric } from '@/js/libs/memo';
import { Select, StyledAsyncCreatableSelect, StyledCreatableSelect, AsyncSelect, MultiSelect, AsyncMultiSelect, InputIconsRenderer, FormField, isErrorVisible, AsyncCreatableMultiSelect, CreatableMultiSelect, } from '@/js/libs/ui/src/V2';
import { useController } from '../hooks';
function omit(obj, keys) {
    return Object.entries(obj).reduce((acc, [key, val]) => {
        if (!keys.includes(key)) {
            // @ts-expect-error - Object.entries have bad typings
            acc[key] = val;
        }
        return acc;
    }, {});
}
function createSelectField(Component) {
    const PrimitivesAdapter = (() => {
        const original = Symbol('originalValue');
        return {
            box: (value) => {
                if (typeof value === 'number' || typeof value === 'string') {
                    return { label: String(value), value, [original]: value };
                }
                return value;
            },
            unbox: (value) => {
                if (typeof value === 'object' && value !== null && original in value) {
                    return value[original];
                }
                return value;
            },
        };
    })();
    function useUnboxedCallbacks(props) {
        const getOptionValue = useCallbackRef((option) => 
        // @ts-expect-error - PrimitivesAdapter.unbox returns the original value at runtime
        props.getOptionValue(PrimitivesAdapter.unbox(option)));
        const getOptionLabel = useCallbackRef((option) => 
        // @ts-expect-error - PrimitivesAdapter.unbox returns the original value at runtime
        props.getOptionLabel(PrimitivesAdapter.unbox(option)));
        const isOptionSelected = useCallbackRef((option, selectValue) => props.isOptionSelected(
        // @ts-expect-error - PrimitivesAdapter.unbox returns the original value at runtime
        PrimitivesAdapter.unbox(option), Array.isArray(selectValue)
            ? selectValue.map((v) => PrimitivesAdapter.unbox(v))
            : PrimitivesAdapter.unbox(selectValue)));
        return {
            ...(props.getOptionValue && { getOptionValue }),
            ...(props.getOptionLabel && { getOptionLabel }),
            ...(props.isOptionSelected && { isOptionSelected }),
        };
    }
    // https://github.com/JedWatson/react-select/issues/5032
    function useAdoptPrimitiveValues(props, field) {
        const options = useMemo(() => {
            if (Array.isArray(props.options)) {
                return props.options.map((v) => PrimitivesAdapter.box(v));
            }
            return props.options;
        }, [props.options]);
        const value = useMemo(() => {
            if (Array.isArray(field.value)) {
                return field.value.map((v) => PrimitivesAdapter.box(v));
            }
            return PrimitivesAdapter.box(field.value);
        }, [field.value]);
        const onChange = useCallbackRef(async (newValue, actionMeta) => {
            const unboxedValue = Array.isArray(newValue)
                ? newValue.map((v) => PrimitivesAdapter.unbox(v))
                : PrimitivesAdapter.unbox(newValue);
            if (await (props.cofirmChange?.(unboxedValue, value) ?? true)) {
                props.onChange?.(unboxedValue, actionMeta);
                field.onChange(unboxedValue);
            }
        });
        return {
            value,
            options,
            onChange,
            ...useUnboxedCallbacks(props),
        };
    }
    const FormComponent = memoGeneric((props) => {
        const { control, name, label, labelTooltip, required, isDisabled, disabled: disabledProp, renderError, iconStart, iconEnd, withTouched = false, warningMessage, onBlur: onBlurProp, ...restProps } = omit(props, ['options', 'onChange']);
        const { field, fieldState } = useController({ control, name });
        const { isTouched, error } = fieldState;
        const { ref: innerRef, ...restField } = omit(field, ['onChange', 'value']);
        const overriddenProps = useAdoptPrimitiveValues(props, field);
        const handleBlur = useCallbackRef((event) => {
            onBlurProp?.(event);
            field.onBlur();
        });
        const disabled = isDisabled || disabledProp;
        return (_jsx(FormField, { name: name, touched: isTouched, error: error?.message, disabled: disabled, required: required, label: label, labelTooltip: labelTooltip, renderError: renderError, warningMessage: warningMessage, withTouched: withTouched, children: _jsx(InputIconsRenderer, { iconStart: iconStart, iconEnd: iconEnd, children: _jsx(Component, { isDisabled: disabled, error: isErrorVisible(error?.message, isTouched, withTouched), warning: !!warningMessage, ...restField, ...restProps, ...overriddenProps, innerRef: innerRef, onBlur: handleBlur }) }) }));
    });
    return FormComponent;
}
export const SelectField = createSelectField(Select);
export const AsyncCreatableSelectField = createSelectField(StyledAsyncCreatableSelect);
export const CreatableSelectField = createSelectField(StyledCreatableSelect);
export const CreatableMultiSelectField = createSelectField(CreatableMultiSelect);
export const AsyncCreatableMultiSelectField = createSelectField(AsyncCreatableMultiSelect);
export const AsyncSelectField = createSelectField(AsyncSelect);
export const MultiSelectField = createSelectField(MultiSelect);
export const AsyncMultiSelectField = createSelectField(AsyncMultiSelect);
